import { UserOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useSelector } from "../../../context/AppContext";
import styled from "styled-components";
import MatchesAPI from "../../../apis/matches";
import { useUrlParams } from "../../../utils/use-url-params";
import { prettyCount } from "../../../utils/format";
import { DEFAULT_FMAGIC, DNI_MACROS } from "../common/constant";
import MapView from "../common/map-view";
import MiniTable from "../common/mini-table";
import { buildMultiMatchesPayloadForCountry, buildMultiMatchesV2PayloadForCountry, buildOverviewData, isUSICP } from "../common/util";
import { BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import GenderBar from "../common/gender-bar";

const OverviewWrapper = styled.div``;

const ReportBar1 = styled.div`
  background-color: #F7F8FA;
  border-radius: 8px;
  margin-top: 10px;
`;

const MatchPool = styled.div<{ $deiDisabled?: boolean }>`
  ${({ $deiDisabled }) =>
    $deiDisabled &&
    `
      display: flex;
      justify-content: center;
      align-items: center;
    `}
`;

const Count = styled.div<{ $deiDisabled?: boolean }>`
  margin: 5px 15px;
  width: 97px;
  display: inline-block;
  border-right: ${({ $deiDisabled }) => ($deiDisabled ? 'none' : '1px solid #E0E0E0')};
  text-align: center;
`;

const CountNumber = styled.div`
  color: #308DF8;
  font-size: 27px;
  font-weight: 700;
`;

const CountLabel = styled.div`
  color: #5B5B5B;
  position: relative;
  display: inline;
  top: -8px;
`;

const CountSubLabel = styled.div`
  font-size: 8px;
  color: #808080;
`;

const DI = styled.div`
  width: 720px;
  display: inline-block;
  vertical-align: top;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const DiversityRow = styled.div`
  display: flex;
`;

const DiversityLabel = styled.div`
  display: inline-block;
  font-weight: 700;
  font-size: 12px;
`;

const DiversityContent = styled.div`
  display: inline-block;
  font-size: 10px;
  margin-left: 8px;
`;

const GeoReport = styled.div`
  background-color: #F7F8FA;
  border-radius: 8px;
  margin-top: 18px;
  display: flex;
  padding: 20px;
`;

const GeoSection = styled.div`
  padding-right: 20px;
  border-right: 1px solid #E0E0E0;
`;

const MapSection = styled.div`
  padding-right: 20px;
  border-right: 1px solid #E0E0E0;
`;

const TimezoneSection = styled.div`
  padding-right: 20px;
`;

const FMTitle = styled.div`
  color: #313131;
  font-style: normal;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 0px;
`;

const MagicPill = styled.div`
  display: inline-block;
  color: #333333;
  font-weight: 400;
  padding: 2px 8px;
  max-width: 200px;
  border-radius: 40px;
`;

const MagicPillContent = styled.div`
  display: flex;
  float: left;
`;

const MagicIcon = styled(UserOutlined)`
  padding-right: 2px;
  position: relative;
  top: 1px;
`;

const MagicText = styled.span`
  width: 150px;
  font-size: 13px;
`;

const MagicCount = styled.span`
  padding-right: 5px;
  font-weight: 700;
`;

const MagicIconWrapper = styled.span`
  padding-left: 2px;
`;

const MagicContainer = styled.div`
  width: 200px;
  margin-bottom: 10px;
  margin-right: 10px;
  flex: 1 0 21%;
  max-width: 200px;
`;

const MagicDescription = styled.div`
  font-size: 10px;
`;

const MagicSection = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const CircleDot = styled.span<{ $color: string }>`
  height: 8px;
  width: 8px;
  background-color: ${props => props.$color};
  border-radius: 50%;
  display: inline-block;
`;

const LegendItem = styled.span`
  padding: 0 10px;
`;

interface ColumnRecord {
  country?: string;
  state?: string;
  address?: string;
  profile_count: number;
}

const Columns = (column_name: string) => {
  return [
    {
      title: column_name,
      dataIndex: 'country',
      key: 'country',
      render: (_text: string, record: ColumnRecord) => (
        <>{`${record.address ? record.address : record.state ? record.state + ' ' + record.country : record.country}`}</>
      )
    },
    {
      title: 'Matches',
      dataIndex: 'count',
      key: 'count',
      align: 'right' as const,
      render: (_text: string, record: ColumnRecord) => (
        <>{`${prettyCount(record.profile_count)}`}</>
      )
    }
  ];
};

interface EthBarProps {
  data: Record<string, number>;
  totalCount: number;
}

interface BarData {
  name: string;
  "Asians": number;
  "Middle Eastern": number;
  "Caucasian": number;
  "East Asians": number;
  "African American/Black": number;
  "Hispanic": number;
}

const EthBar: React.FC<EthBarProps> = ({ data, totalCount }) => {
  const barData: BarData[] = [{
    name: 'Ethnicity',
    "Asians": (data["cmetrics-south asian"] || 0) + (data["cmetrics-east asian"] || 0),
    "Middle Eastern": data["cmetrics-middle eastern"] || 0,
    "Caucasian": data["cmetrics-caucasian"] || 0,
    "East Asians": data["cmetrics-east asian"] || 0,
    "African American/Black": data["cmetrics-african american"] || 0,
    "Hispanic": data["cmetrics-hispanic"] || 0,
  }];

  const calculatePercentage = (value: number): number => {
    return Math.floor((value * 100) / totalCount);
  };

  return (
    <>
      <BarChart layout="vertical" width={600} height={20} data={barData}>
        <XAxis hide type="number" />
        <YAxis hide dataKey="name" type="category" />
        <Tooltip wrapperStyle={{ zIndex: 1000 }} />
        <Bar dataKey="Caucasian" stackId="a" fill="rgba(38, 77, 121, 1)" radius={[10, 0, 0, 10]} />
        <Bar dataKey="Asians" stackId="a" fill="rgba(38, 77, 121, 0.8)" />
        <Bar dataKey="African American/Black" stackId="a" fill="rgba(38, 77, 121, 0.6)" />
        <Bar dataKey="Hispanic" stackId="a" fill="rgba(38, 77, 121, 0.4)" />
        <Bar dataKey="Middle Eastern" stackId="a" fill="rgba(38, 77, 121, 0.2)" radius={[0, 10, 10, 0]} />
      </BarChart>
      <div>
        <LegendItem>
          <CircleDot $color="rgba(38, 77, 121, 1)" />
          Caucasian ({calculatePercentage(data["cmetrics-caucasian"] || 0)}%)
        </LegendItem>
        <LegendItem>
          <CircleDot $color="rgba(38, 77, 121, 0.8)" />
          Asian ({calculatePercentage((data["cmetrics-south asian"] || 0) + (data["cmetrics-east asian"] || 0))}%)
        </LegendItem>
        <LegendItem>
          <CircleDot $color="rgba(38, 77, 121, 0.6)" />
          African American ({calculatePercentage(data["cmetrics-african american"] || 0)}%)
        </LegendItem>
        <LegendItem>
          <CircleDot $color="rgba(38, 77, 121, 0.4)" />
          Hispanic ({calculatePercentage(data["cmetrics-hispanic"] || 0)}%)
        </LegendItem>
        <LegendItem>
          <CircleDot $color="rgba(38, 77, 121, 0.2)" />
          Middle Eastern ({calculatePercentage(data["cmetrics-middle eastern"] || 0)}%)
        </LegendItem>
      </div>
    </>
  );
};

interface LocationData {
  name: string;
  prfs?: any[];
}

const TransformData = (Obj: Record<string, LocationData[]>, type: string): ColumnRecord[] => {
  const _data: ColumnRecord[] = [];
  Obj[type]?.forEach((obj: LocationData) => {
    _data.push({ country: obj.name, profile_count: obj.prfs?.length || 0 });
  });
  return _data.slice(0, 5);
};

interface OverviewReportProps {
  setPoolCount: (poolCount: number) => void;
  analyzeMatchesData: any;
  currentIcp: any;
  slPubIds?: string[];
  effectiveSearch: any;
  disableDEI: boolean;
  setDisableDEI: React.Dispatch<React.SetStateAction<boolean>>;
}

const OverviewReport: React.FC<OverviewReportProps> = ({
  setPoolCount,
  analyzeMatchesData,
  currentIcp,
  slPubIds,
  effectiveSearch,
  disableDEI,
  setDisableDEI
}) => {
  const all_macros = useSelector((state: any) => state.attrs_state.all_macros);
  const user = useSelector((state: any) => state.login.user);
  const org_settings = useSelector((state: any) => state.analytics.org_settings);
  const [multiMatchesByLoc, setMultiMatchesByLoc] = useState<any>({});
  const [isUSMap] = useState<boolean>(isUSICP(effectiveSearch ?? currentIcp));
  const { joid, sandbox } = useUrlParams();
  const [geoTableData, setGeoTableData] = useState<any>({});
  const _dni = Object.keys(DNI_MACROS);
  const _magic = Object.keys(DEFAULT_FMAGIC);

  useEffect(() => {
    const getMultiMatchesForGeo = async () => {
      const payload = effectiveSearch?.mode === 'v2' ?
        await buildMultiMatchesV2PayloadForCountry(effectiveSearch, isUSMap ? 'United States' : 'Global', [], [..._dni, ..._magic], joid || user.uid, org_settings.domain_to_exclude_search)
        : await buildMultiMatchesPayloadForCountry(effectiveSearch, isUSMap ? 'United States' : 'Global', [], [..._dni, ..._magic], joid || user.uid);
      try {
        if (sandbox) {
          payload['sandbox'] = true;
        }
        if (slPubIds && slPubIds.length > 0) {
          payload['prids'] = slPubIds;
        }
        payload['joid'] = joid || user.uid;
        const response = effectiveSearch?.mode === 'v2' ? await MatchesAPI.makeMultiMatchesV2APICall(payload) : await MatchesAPI.makeMultiMatchesAPICall(payload);
        setMultiMatchesByLoc(response.data);
        const _formated_data = buildOverviewData(response.data);
        setPoolCount(response.data['base_filter_count']);
        setGeoTableData(_formated_data);
        setDisableDEI(response.data['disable_dni']);
      } catch (err) {
        console.error(err);
      }
    };
    if (effectiveSearch?.cxt_data) {
      getMultiMatchesForGeo();
    }
  }, [effectiveSearch, org_settings, slPubIds]);

  return (
    <>
      {multiMatchesByLoc?.base_filter_count && (
        <OverviewWrapper className="overview-wrapper">
          <ReportBar1>
            <MatchPool $deiDisabled={disableDEI}>
              <Count $deiDisabled={disableDEI}>
                <CountNumber>
                  {multiMatchesByLoc?.base_filter_count ? prettyCount(multiMatchesByLoc?.base_filter_count) : 0}
                </CountNumber>
                <CountLabel>
                  Match Pool
                  <CountSubLabel>(Incl. filtered matches)</CountSubLabel>
                </CountLabel>
              </Count>
              {!disableDEI && (
                <DI>
                {geoTableData?._diversity_n_magic && (
                  <DiversityRow>
                    <DiversityLabel>Gender</DiversityLabel>
                    <DiversityContent>
                      <GenderBar data={geoTableData._diversity_n_magic.dni} totalCount={multiMatchesByLoc['base_filter_count']} />
                    </DiversityContent>
                  </DiversityRow>
                )}
                {geoTableData?._diversity_n_magic && (
                  <DiversityRow>
                    <DiversityLabel>Ethnicity</DiversityLabel>
                    <DiversityContent>
                      <EthBar data={geoTableData._diversity_n_magic.dni} totalCount={multiMatchesByLoc['base_filter_count']} />
                    </DiversityContent>
                  </DiversityRow>
                )}
                </DI>
              )}
            </MatchPool>
          </ReportBar1>
          <GeoReport>
            {multiMatchesByLoc['base_filter_count'] && (
              <MapSection>
                <MapView data={multiMatchesByLoc} isGlobalMap={!isUSMap} />
              </MapSection>
            )}
            {geoTableData?._countries && (
              <GeoSection>
                <MiniTable title="Top 5 Locations" columns={Columns('Location')} data={geoTableData._countries.slice(0, 5)} />
              </GeoSection>
            )}
            {analyzeMatchesData?.top_locations && (
              <TimezoneSection>
                <MiniTable
                  title="Top 5 Timezones"
                  columns={Columns('Timezone')}
                  data={TransformData(analyzeMatchesData, 'top_timezone')}
                  probabilistic={multiMatchesByLoc['base_filter_count'] > 10000}
                />
              </TimezoneSection>
            )}
          </GeoReport>
          {geoTableData?._diversity_n_magic && (
            <>
              <FMTitle>Findem Magic Attributes</FMTitle>
              <MagicSection>
                {geoTableData?._diversity_n_magic?.magic && _magic.map((macro: string, idx: number) => (
                  geoTableData?._diversity_n_magic?.magic[macro] > 0 ? (
                    <MagicContainer key={`magic-container-${idx}`}>
                      <MagicPill className={`pill-bg-${idx}`}>
                        <MagicPillContent>
                          <MagicIcon />
                          <MagicText>
                            <MagicCount>{prettyCount(geoTableData?._diversity_n_magic?.magic[macro] || 0)}</MagicCount>
                            {DEFAULT_FMAGIC[macro]}
                          </MagicText>
                          <MagicIconWrapper>
                            <i className="fa fa-magic" />
                          </MagicIconWrapper>
                        </MagicPillContent>
                      </MagicPill>
                      <MagicDescription>
                        {unescape(all_macros[macro]?.description || all_macros[macro]?.name)}
                      </MagicDescription>
                    </MagicContainer>
                  ) : null
                ))}
              </MagicSection>
            </>
          )}
        </OverviewWrapper>
      )}
    </>
  );
};

export default OverviewReport;
